// File: app/javascript/packs/timeline_calculator.js

document.addEventListener('turbolinks:load', () => {
  // 1. Check if required DOM elements exist
  const container = document.getElementById('timeline-calculator-container');
  const calculateBtn = document.getElementById('calculate-timeline-btn');
  const startDateInput = document.getElementById('start-date');
  const startDateLabel = document.getElementById('start-date-label');
  const resultsSection = document.getElementById('timeline-results');
  const timelineOutput = document.getElementById('timeline-output');

  // 2. Early return if elements don't exist
  if (!container || !calculateBtn || !startDateInput || !resultsSection || !timelineOutput) return;

  // Get mailing flags from data attributes
  const hasElectronicVoting = container.dataset.electronicVoting === 'true';
  const hasRequestForms = container.dataset.requestForms === 'true';
  const hasPreBallots = container.dataset.preBallots === 'true';
  const hasBallots = container.dataset.ballots === 'true';

  // Determine the starting point (first enabled mailing in order)
  // Order: Electronic Consent -> Nomination Forms -> Pre-Ballot -> Ballots
  let startingPoint = null;
  if (hasElectronicVoting) {
    startingPoint = 'electronicConsent';
  } else if (hasRequestForms) {
    startingPoint = 'nominationForm';
  } else if (hasPreBallots) {
    startingPoint = 'preBallot';
  } else if (hasBallots) {
    startingPoint = 'ballot';
  }

  // If no mailings enabled, disable the form
  if (!startingPoint) {
    calculateBtn.disabled = true;
    calculateBtn.classList.add('opacity-50', 'cursor-not-allowed');
    startDateInput.disabled = true;
    if (startDateLabel) {
      startDateLabel.textContent = 'No mailings enabled for this order';
    }
    return;
  }

  // Set the label based on starting point
  const labelMap = {
    electronicConsent: 'Electronic Consent Mailing Date',
    nominationForm: 'Nomination Form Mailing Date',
    preBallot: 'Pre-Ballot Notification Mailing Date',
    ballot: 'Ballot Mailing Date'
  };
  if (startDateLabel) {
    startDateLabel.textContent = labelMap[startingPoint];
  }

  // Timeline deltas (matching Ruby constants)
  const ELECTION_STATUS_NOMINATION_FORM = 32; // Electronic Consent to Nomination Form
  const NOMINATION_FORM_PRE_BALLOT = 32;      // Nomination Form to Pre-Ballot
  const PRE_BALLOT_BALLOT = 30;               // Pre-Ballot to Ballot
  const BALLOT_DAY = 32;                      // Ballot to Election Day

  // Hardcoded off-limit month/day pairs (from ElectionScheduler::ARRAY_OF_MONTH_DAYS)
  const OFF_LIMIT_MONTH_DAYS = [
    { month: 11, day: 14 }, // November 14
    { month: 12, day: 5 },  // December 5
    { month: 12, day: 13 }, // December 13
    { month: 12, day: 22 }, // December 22
    { month: 12, day: 23 }, // December 23
    { month: 12, day: 24 }, // December 24
    { month: 12, day: 25 }, // December 25
    { month: 12, day: 26 }, // December 26
    { month: 12, day: 27 }, // December 27
    { month: 4, day: 2 },   // April 2
    { month: 8, day: 11 },  // August 11
  ];

  // Get current date in Los Angeles timezone
  function getTodayInLA() {
    const now = new Date();
    const laDateStr = now.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    const [year, month, day] = laDateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Format date as YYYY-MM-DD for input min attribute
  function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Set minimum date to today in LA timezone
  const todayLA = getTodayInLA();
  const minDateStr = formatDateForInput(todayLA);
  startDateInput.setAttribute('min', minDateStr);

  // US Federal Holidays
  function getUSHolidays(year) {
    const holidays = [];
    holidays.push(new Date(year, 0, 1)); // New Year's Day
    holidays.push(getNthWeekdayOfMonth(year, 0, 1, 3)); // MLK Day
    holidays.push(getNthWeekdayOfMonth(year, 1, 1, 3)); // Presidents Day
    holidays.push(getLastWeekdayOfMonth(year, 4, 1)); // Memorial Day
    holidays.push(new Date(year, 5, 19)); // Juneteenth
    holidays.push(new Date(year, 6, 4)); // Independence Day
    holidays.push(getNthWeekdayOfMonth(year, 8, 1, 1)); // Labor Day
    holidays.push(getNthWeekdayOfMonth(year, 9, 1, 2)); // Columbus Day
    holidays.push(new Date(year, 10, 11)); // Veterans Day
    holidays.push(getNthWeekdayOfMonth(year, 10, 4, 4)); // Thanksgiving
    holidays.push(new Date(year, 11, 25)); // Christmas
    return holidays;
  }

  function getNthWeekdayOfMonth(year, month, weekday, n) {
    const firstDay = new Date(year, month, 1);
    let dayOfWeek = firstDay.getDay();
    let diff = weekday - dayOfWeek;
    if (diff < 0) diff += 7;
    const firstOccurrence = 1 + diff;
    const nthOccurrence = firstOccurrence + (n - 1) * 7;
    return new Date(year, month, nthOccurrence);
  }

  function getLastWeekdayOfMonth(year, month, weekday) {
    const lastDay = new Date(year, month + 1, 0);
    let dayOfWeek = lastDay.getDay();
    let diff = dayOfWeek - weekday;
    if (diff < 0) diff += 7;
    return new Date(year, month, lastDay.getDate() - diff);
  }

  function isUSHoliday(date) {
    const year = date.getFullYear();
    const holidays = getUSHolidays(year);
    return holidays.some(holiday =>
      holiday.getFullYear() === date.getFullYear() &&
      holiday.getMonth() === date.getMonth() &&
      holiday.getDate() === date.getDate()
    );
  }

  function isOffLimitMonthDay(date) {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return OFF_LIMIT_MONTH_DAYS.some(md => md.month === month && md.day === day);
  }

  function isWeekend(date) {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  function isPossibleDay(date) {
    if (isOffLimitMonthDay(date)) return false;
    if (isWeekend(date)) return false;
    if (isUSHoliday(date)) return false;
    return true;
  }

  function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  function calculateDateForward(currentDate, delta) {
    let adjustedDate = addDays(currentDate, delta);
    while (!isPossibleDay(adjustedDate)) {
      adjustedDate = addDays(adjustedDate, 1);
    }
    return adjustedDate;
  }

  function formatDate(date) {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  // Calculate timeline based on starting point and enabled mailings
  function calculateTimeline(startDate) {
    const results = {};
    let currentDate = startDate;

    // Start from the appropriate point based on what's enabled
    if (startingPoint === 'electronicConsent') {
      results.electronicConsentDate = currentDate;
      if (hasRequestForms) {
        results.nominationFormDate = calculateDateForward(currentDate, ELECTION_STATUS_NOMINATION_FORM);
        currentDate = results.nominationFormDate;
      }
      if (hasPreBallots) {
        if (hasRequestForms) {
          results.preBallotDate = calculateDateForward(currentDate, NOMINATION_FORM_PRE_BALLOT);
        } else {
          // Skip nomination form delta, go directly from electronic consent
          results.preBallotDate = calculateDateForward(results.electronicConsentDate, ELECTION_STATUS_NOMINATION_FORM + NOMINATION_FORM_PRE_BALLOT);
        }
        currentDate = results.preBallotDate;
      }
      if (hasBallots) {
        if (hasPreBallots) {
          results.ballotDate = calculateDateForward(currentDate, PRE_BALLOT_BALLOT);
        } else if (hasRequestForms) {
          results.ballotDate = calculateDateForward(currentDate, NOMINATION_FORM_PRE_BALLOT + PRE_BALLOT_BALLOT);
        } else {
          results.ballotDate = calculateDateForward(results.electronicConsentDate, ELECTION_STATUS_NOMINATION_FORM + NOMINATION_FORM_PRE_BALLOT + PRE_BALLOT_BALLOT);
        }
        currentDate = results.ballotDate;
        results.electionDay = calculateDateForward(currentDate, BALLOT_DAY);
      }
    } else if (startingPoint === 'nominationForm') {
      results.nominationFormDate = currentDate;
      if (hasPreBallots) {
        results.preBallotDate = calculateDateForward(currentDate, NOMINATION_FORM_PRE_BALLOT);
        currentDate = results.preBallotDate;
      }
      if (hasBallots) {
        if (hasPreBallots) {
          results.ballotDate = calculateDateForward(currentDate, PRE_BALLOT_BALLOT);
        } else {
          results.ballotDate = calculateDateForward(results.nominationFormDate, NOMINATION_FORM_PRE_BALLOT + PRE_BALLOT_BALLOT);
        }
        currentDate = results.ballotDate;
        results.electionDay = calculateDateForward(currentDate, BALLOT_DAY);
      }
    } else if (startingPoint === 'preBallot') {
      results.preBallotDate = currentDate;
      if (hasBallots) {
        results.ballotDate = calculateDateForward(currentDate, PRE_BALLOT_BALLOT);
        currentDate = results.ballotDate;
        results.electionDay = calculateDateForward(currentDate, BALLOT_DAY);
      }
    } else if (startingPoint === 'ballot') {
      results.ballotDate = currentDate;
      results.electionDay = calculateDateForward(currentDate, BALLOT_DAY);
    }

    return results;
  }

  // Render the timeline results
  function renderResults(results) {
    let html = '<div class="space-y-3">';

    if (results.electronicConsentDate) {
      const isStart = startingPoint === 'electronicConsent';
      html += createTimelineItem('Electronic Consent Mailing Date', results.electronicConsentDate, isStart ? 'Starting point' : '');
    }

    if (results.nominationFormDate) {
      const isStart = startingPoint === 'nominationForm';
      const delta = isStart ? 'Starting point' : `+${ELECTION_STATUS_NOMINATION_FORM} days`;
      html += createTimelineItem('Nomination Form Mailing Date', results.nominationFormDate, delta);
    }

    if (results.preBallotDate) {
      const isStart = startingPoint === 'preBallot';
      let delta = 'Starting point';
      if (!isStart) {
        if (results.nominationFormDate) {
          delta = `+${NOMINATION_FORM_PRE_BALLOT} days`;
        } else if (results.electronicConsentDate) {
          delta = `+${ELECTION_STATUS_NOMINATION_FORM + NOMINATION_FORM_PRE_BALLOT} days`;
        }
      }
      html += createTimelineItem('Pre-Ballot Notification Date', results.preBallotDate, delta);
    }

    if (results.ballotDate) {
      const isStart = startingPoint === 'ballot';
      let delta = 'Starting point';
      if (!isStart) {
        if (results.preBallotDate) {
          delta = `+${PRE_BALLOT_BALLOT} days`;
        } else if (results.nominationFormDate) {
          delta = `+${NOMINATION_FORM_PRE_BALLOT + PRE_BALLOT_BALLOT} days`;
        } else if (results.electronicConsentDate) {
          delta = `+${ELECTION_STATUS_NOMINATION_FORM + NOMINATION_FORM_PRE_BALLOT + PRE_BALLOT_BALLOT} days`;
        }
      }
      html += createTimelineItem('Ballot Mailing Date', results.ballotDate, delta);
    }

    if (results.electionDay) {
      html += createTimelineItem('Election Day', results.electionDay, `+${BALLOT_DAY} days`, true);

      // Show the Set Election Date form and populate the hidden field
      const setElectionDateForm = document.getElementById('set-election-date-form');
      const electionDateInput = document.getElementById('election-date-input');
      if (setElectionDateForm && electionDateInput) {
        electionDateInput.value = formatDateForInput(results.electionDay);
        setElectionDateForm.classList.remove('hidden');
      }
    } else {
      // Hide the form if no election day calculated
      const setElectionDateForm = document.getElementById('set-election-date-form');
      if (setElectionDateForm) {
        setElectionDateForm.classList.add('hidden');
      }
    }

    html += '</div>';

    timelineOutput.innerHTML = html;
    resultsSection.classList.remove('hidden');
  }

  function createTimelineItem(label, date, delta, isHighlighted = false) {
    const bgClass = isHighlighted ? 'bg-[#491363] text-white' : 'bg-gray-50';
    const textClass = isHighlighted ? 'text-white' : 'text-gray-800';
    const subTextClass = isHighlighted ? 'text-purple-200' : 'text-gray-500';

    return `
      <div class="flex items-center justify-between p-4 ${bgClass} rounded-lg">
        <div>
          <p class="font-medium ${textClass}">${label}</p>
          <p class="text-xs ${subTextClass}">${delta}</p>
        </div>
        <p class="font-semibold ${textClass}">${formatDate(date)}</p>
      </div>
    `;
  }

  function isDateBeforeToday(date) {
    const today = getTodayInLA();
    const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return inputDate < todayMidnight;
  }

  // Event listener for calculate button
  calculateBtn.addEventListener('click', () => {
    const startDateValue = startDateInput.value;

    if (!startDateValue) {
      alert('Please select a start date.');
      return;
    }

    const [year, month, day] = startDateValue.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);

    if (isDateBeforeToday(startDate)) {
      alert('The selected date cannot be before today. Please select today or a future date.');
      startDateInput.value = '';
      return;
    }

    if (!isPossibleDay(startDate)) {
      alert('The selected start date falls on a weekend, holiday, or off-limit day. Please choose a valid business day.');
      return;
    }

    const results = calculateTimeline(startDate);
    renderResults(results);
  });

  // Validate on date input change
  startDateInput.addEventListener('change', () => {
    const startDateValue = startDateInput.value;
    if (!startDateValue) return;

    const [year, month, day] = startDateValue.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);

    if (isDateBeforeToday(startDate)) {
      alert('The selected date cannot be before today. Please select today or a future date.');
      startDateInput.value = '';
      resultsSection.classList.add('hidden');
    }
  });
});
